import React, { useContext, useMemo, useState } from 'react';
import { AppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useAllOrders } from '../../context/AllOrdersContext';
import { useNotifications } from '../../context/NotificationContext';
import { parseDate, formatDateReadable, getStartOfWeek, getTimeRemaining } from '../../utils/dateUtils';
import NotificationOverlay from '../../components/NotificationOverlay';
import { ClipboardListIcon, OrderIcon, AccountIcon, RestaurantIcon, ChartBarIcon, StarIcon, ShieldCheckIcon, ClockIcon, CalendarIcon } from '../../components/icons';



const AdminDashboardScreen: React.FC = () => {
    const { setView } = useContext(AppContext);
    const { currentUser } = useAuth();
    const { restaurants, getRestaurantById, refreshRestaurants, loading: dataLoading } = useData();
    const { placedOrders } = useAllOrders();
    const { unreadCount, stopAlarm } = useNotifications();
    const [showNotifications, setShowNotifications] = useState(false);

    // Recherche robuste : d'abord par ID explicite, sinon par ownerId (propriétaire)
    const restaurant = getRestaurantById(currentUser?.restaurantId || '') || restaurants.find(r => r.ownerId === currentUser?.uid);

    const subActive = useMemo(() => {
        if (!restaurant) return false;
        const isActive = restaurant.subscriptionStatus === 'active' || restaurant.subscriptionStatus === 'trial';
        if (!isActive) return false;
        
        const expiry = restaurant.subscriptionExpiry || restaurant.trialExpiry || restaurant.currentPeriodExpiry;
        if (!expiry) return true;
        
        return parseDate(expiry).getTime() > new Date().getTime();
    }, [restaurant]);

    const featuredActive = useMemo(() => {
        if (!restaurant || restaurant.featuredStatus !== 'active' || !restaurant.featuredExpiry) return false;
        return parseDate(restaurant.featuredExpiry).getTime() > new Date().getTime();
    }, [restaurant]);

    const menuItems = [
        { screen: 'adminAccount', label: 'Mon Restaurant', icon: RestaurantIcon, color: 'bg-indigo-100', textColor: 'text-indigo-800' },
        { screen: 'adminDishes', label: 'Gérer le Menu', icon: ClipboardListIcon, color: 'bg-blue-100', textColor: 'text-blue-800' },
        { screen: 'adminOrders', label: 'Commandes', icon: OrderIcon, color: 'bg-green-100', textColor: 'text-green-800' },
        { screen: 'adminReservations', label: 'Réservations', icon: CalendarIcon, color: 'bg-purple-100', textColor: 'text-purple-800' },
        { screen: 'adminAccounting', label: 'Comptabilité', icon: ChartBarIcon, color: 'bg-yellow-100', textColor: 'text-yellow-800' },
        { screen: 'adminDrivers', label: 'Mes Livreurs', icon: AccountIcon, color: 'bg-red-100', textColor: 'text-red-800' }
    ];

    // --- DATA AGGREGATION LOGIC ---
    const stats = useMemo(() => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const startOfWeek = getStartOfWeek();

        const realizedOrders = placedOrders.filter(o => o.status === 'Livrée' || o.status === 'Terminée');
        const todayRealizedOrders = realizedOrders.filter(o => parseDate(o.timestamp).getTime() >= startOfToday);
        const weeklyRealizedOrders = realizedOrders.filter(o => parseDate(o.timestamp).getTime() >= startOfWeek.getTime());
        const monthlyRealizedOrders = realizedOrders.filter(o => {
            const date = parseDate(o.timestamp);
            return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
        });

        // Activity includes all successful stages for the graph
        const activityOrders = placedOrders.filter(o => ['Envoyée', 'Livrée', 'Terminée', 'Prête'].includes(o.status));

        const totalRevenue = realizedOrders.reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);
        const todayRevenue = todayRealizedOrders.reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);
        const weeklyRevenue = weeklyRealizedOrders.reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);
        const monthlyRevenue = monthlyRealizedOrders.reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);

        const totalOrdersCount = realizedOrders.length;

        // Top Dishes
        const dishCounts: Record<string, number> = {};
        realizedOrders.forEach(order => {
            order.items.forEach(item => {
                const isPack = item.type === 'pack';
                const name = isPack ? (item.pack?.name || 'Pack') : (item.dish?.name || 'Plat');
                if (name) {
                    dishCounts[name] = (dishCounts[name] || 0) + item.quantity;
                }
            });
        });
        const topDishes = Object.entries(dishCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([name, count]) => ({ name, count }));

        // Peak Hours (0-23)
        const hoursDistribution = new Array(24).fill(0);
        activityOrders.forEach(order => {
            const hour = parseDate(order.timestamp).getHours();
            hoursDistribution[hour]++;
        });
        const maxOrdersInHour = Math.max(...hoursDistribution, 1);

        return {
            totalRevenue,
            todayRevenue,
            weeklyRevenue,
            monthlyRevenue,
            totalOrders: totalOrdersCount,
            todayOrdersCount: todayRealizedOrders.length,
            averageBasket: realizedOrders.length > 0 ? Math.round(totalRevenue / realizedOrders.length) : 0,
            topDishes,
            hoursDistribution,
            maxOrdersInHour
        };
    }, [placedOrders]);

    if (dataLoading) {
        return (
            <div className="p-8 text-center min-h-screen bg-gray-50 flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-red-100 border-t-[#E31E24] rounded-full animate-spin mb-4"></div>
                <h2 className="text-xl font-bold text-gray-700">Chargement des données...</h2>
            </div>
        );
    }

    if (!restaurant) {
        return (
            <div className="p-8 text-center min-h-screen bg-gray-50 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4 animate-pulse">
                    <span className="text-3xl">🏗️</span>
                </div>
                <h2 className="text-xl font-bold text-gray-700">Initialisation de votre restaurant...</h2>
                <p className="text-gray-500 mt-2 mb-6">Nous finalisons la création de votre espace.</p>

                <button
                    onClick={() => refreshRestaurants()}
                    className="px-6 py-2 bg-green-600 text-white font-bold rounded-full hover:bg-green-700 transition-colors shadow-lg"
                >
                    Rafraîchir maintenant
                </button>
                <p className="text-xs text-gray-400 mt-4">Si le problème persiste, essayez de vous reconnecter.</p>
            </div>
        )
    }

    const subRemaining = getTimeRemaining(restaurant.subscriptionExpiry || restaurant.trialExpiry);
    const featuredRemaining = featuredActive ? getTimeRemaining(restaurant.featuredExpiry) : null;

    return (
        <div className="p-4 bg-gray-50 min-h-screen pb-24">
            {/* Status Hub - Consolidated Premium Version */}
            <div className="mb-6 grid grid-cols-2 gap-3">
                {/* Visibility Status */}
                <div onClick={() => setView({ screen: 'adminAccount' })} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex items-center gap-3 cursor-pointer active:scale-95 transition-all">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${
                        restaurant.status === 'suspended' ? 'bg-red-50 text-red-600' : 
                        restaurant.isOnline === false ? 'bg-orange-50 text-orange-600' : 
                        restaurant.status === 'approved' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'
                    }`}>
                        {restaurant.status === 'suspended' ? '🔴' : 
                         restaurant.isOnline === false ? '🟠' :
                         restaurant.status === 'approved' ? '🟢' : '🟡'}
                    </div>
                    <div className="flex-1 overflow-hidden">
                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-[0.1em] leading-none mb-1">Visibilité</p>
                        <p className="text-[10px] font-black text-gray-900 uppercase italic truncate">
                            {restaurant.status === 'suspended' ? 'Masqué' : 
                             restaurant.isOnline === false ? 'Fermé' :
                             restaurant.status === 'approved' ? 'En Ligne' : 'Attente'}
                        </p>
                    </div>
                </div>

                {/* Subscription Status */}
                <div onClick={() => setView({ screen: 'adminAccount' })} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex items-center gap-3 cursor-pointer active:scale-95 transition-all">
                    <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center text-lg">
                        🛡️
                    </div>
                    <div className="flex-1 overflow-hidden">
                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-[0.1em] leading-none mb-1">Abonnement</p>
                        <p className={`text-[10px] font-black uppercase italic truncate ${subActive ? 'text-gray-900' : 'text-red-600'}`}>
                            {subActive ? (subRemaining || 'Actif') : 'Expiré'}
                        </p>
                    </div>
                </div>

                {/* Delivery Warning (Only if disabled) */}
                {restaurant.deliveryEnabled === false && (
                    <div onClick={() => setView({ screen: 'adminAccount' })} className="bg-orange-500 rounded-2xl p-4 shadow-lg shadow-orange-100 flex items-center gap-3 cursor-pointer active:scale-95 transition-all col-span-2">
                        <div className="w-10 h-10 rounded-xl bg-white/20 text-white flex items-center justify-center text-lg animate-pulse">
                            🥡
                        </div>
                        <div className="flex-1">
                            <p className="text-[8px] font-black text-white/70 uppercase tracking-[0.1em] leading-none mb-1">Livraison Désactivée</p>
                            <p className="text-[10px] font-black text-white uppercase italic">
                                Mode À Emporter Uniquement
                            </p>
                        </div>
                        <div className="bg-white px-3 py-1.5 rounded-lg text-[8px] font-black text-orange-600 uppercase tracking-widest">
                            Activer
                        </div>
                    </div>
                )}

                {/* Featured Status (Only if active) */}
                {featuredActive && (
                    <div className="bg-blue-600 rounded-2xl p-4 shadow-lg shadow-blue-100 flex items-center gap-3 col-span-2">
                        <div className="w-10 h-10 rounded-xl bg-white/20 text-white flex items-center justify-center text-lg">
                            ⭐
                        </div>
                        <div className="flex-1">
                            <p className="text-[8px] font-black text-white/70 uppercase tracking-[0.1em] leading-none mb-1">Promotion Premium</p>
                            <p className="text-[10px] font-black text-white uppercase italic">
                                En Vedette : {featuredRemaining || 'Actif'}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <header className="py-4 flex justify-between items-start">
                <div className="flex flex-col">
                    <h1 className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic leading-none">Tableau de Bord</h1>
                    <p className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] italic mt-1">
                        Bien retour, {restaurant.name} 👋
                    </p>
                </div>
                <div className="relative">
                    <button
                        onClick={() => setShowNotifications(!showNotifications)}
                        className="p-2.5 rounded-xl bg-white shadow-sm border border-gray-100 text-gray-600 hover:bg-gray-50 transition-all relative"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white border-2 border-white shadow-sm">
                                {unreadCount}
                            </span>
                        )}
                    </button>
                    <NotificationOverlay
                        isOpen={showNotifications}
                        onClose={() => setShowNotifications(false)}
                        color="bg-[#E31E24]"
                    />
                </div>
            </header>

            {/* Quick Actions Grid */}
            <div className="grid grid-cols-2 gap-3 mb-6">
                {menuItems.map(item => (
                    <button
                        key={item.label}
                        onClick={() => setView({ screen: item.screen as any })}
                        className={`p-4 ${item.color} rounded-xl shadow-sm hover:shadow-md transition-all active:scale-95 flex flex-col items-center justify-center text-center`}
                    >
                        <item.icon className={`w-8 h-8 mb-2 ${item.textColor}`} />
                        <span className={`text-xs font-bold leading-tight ${item.textColor}`}>{item.label}</span>
                    </button>
                ))}
            </div>

            {/* Active Orders Summary Cards */}
            <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-6 mt-8">État des commandes</h3>
            <div className="grid grid-cols-2 gap-3 mb-6">
                {/* En attente */}
                <div onClick={() => { stopAlarm(); setView({ screen: 'adminOrders', params: { initialTab: 'pending' } }); }} className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-yellow-400 relative overflow-hidden active:scale-95 transition-transform cursor-pointer">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">En attente</p>
                            <p className="text-3xl font-black text-gray-800 mt-1">
                                {placedOrders.filter(o => o.status === 'En attente').length}
                            </p>
                        </div>
                        <div className="p-2 bg-yellow-100 rounded-lg text-yellow-700">
                            <span className="text-xl">🔔</span>
                        </div>
                    </div>
                </div>

                {/* En préparation */}
                <div onClick={() => { stopAlarm(); setView({ screen: 'adminOrders', params: { initialTab: 'in_progress' } }); }} className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-blue-500 relative overflow-hidden active:scale-95 transition-transform cursor-pointer">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">En cuisine</p>
                            <p className="text-3xl font-black text-gray-800 mt-1">
                                {placedOrders.filter(o => o.status === 'En préparation').length}
                            </p>
                        </div>
                        <div className="p-2 bg-blue-100 rounded-lg text-blue-700">
                            <span className="text-xl">👨‍🍳</span>
                        </div>
                    </div>
                </div>

                {/* Prête (Pick up) */}
                <div onClick={() => { stopAlarm(); setView({ screen: 'adminOrders', params: { initialTab: 'in_progress' } }); }} className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-purple-500 relative overflow-hidden active:scale-95 transition-transform cursor-pointer">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Prête (Pick-up)</p>
                            <p className="text-3xl font-black text-gray-800 mt-1">
                                {placedOrders.filter(o => o.status === 'Prête').length}
                            </p>
                        </div>
                        <div className="p-2 bg-purple-100 rounded-lg text-purple-700">
                            <span className="text-xl">🛍️</span>
                        </div>
                    </div>
                </div>

                {/* En livraison */}
                <div onClick={() => { stopAlarm(); setView({ screen: 'adminOrders', params: { initialTab: 'sent' } }); }} className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-green-500 relative overflow-hidden active:scale-95 transition-transform cursor-pointer">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">En livraison</p>
                            <p className="text-3xl font-black text-gray-800 mt-1">
                                {placedOrders.filter(o => o.status === 'Envoyée').length}
                            </p>
                        </div>
                        <div className="p-2 bg-green-100 rounded-lg text-green-700">
                            <span className="text-xl">🛵</span>
                        </div>
                    </div>
                </div>

                {/* Réservations (New) */}
                {restaurant?.tableReservationEnabled && (
                    <div onClick={() => setView({ screen: 'adminReservations' })} className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-indigo-600 relative overflow-hidden active:scale-95 transition-transform cursor-pointer col-span-2">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Réservations de tables</p>
                                <p className="text-[10px] font-black text-gray-400 mt-1 uppercase tracking-tight">Accéder au planning et à la capacité</p>
                            </div>
                            <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                                <CalendarIcon className="w-6 h-6" />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Main Stats Cards (Revenue) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-3 opacity-10">
                        <ChartBarIcon className="w-12 h-12 text-[#E31E24]" />
                    </div>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">C.A. Aujourd'hui</p>
                    <p className="text-xl font-black text-gray-900">{stats.todayRevenue.toLocaleString()} <span className="text-xs font-normal text-gray-400">DA</span></p>
                    <p className="text-[10px] text-green-600 font-bold mt-1">
                        {stats.todayOrdersCount} commande{stats.todayOrdersCount > 1 ? 's' : ''}
                    </p>
                </div>
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-3 opacity-10">
                        <ChartBarIcon className="w-12 h-12 text-[#E31E24]" />
                    </div>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">C.A. Semaine</p>
                    <p className="text-xl font-black text-gray-900">{stats.weeklyRevenue.toLocaleString()} <span className="text-xs font-normal text-gray-400">DA</span></p>
                </div>
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-3 opacity-10">
                        <ChartBarIcon className="w-12 h-12 text-[#E31E24]" />
                    </div>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">C.A. Mois</p>
                    <p className="text-xl font-black text-gray-900">{stats.monthlyRevenue.toLocaleString()} <span className="text-xs font-normal text-gray-400">DA</span></p>
                </div>
            </div>

            {/* Support Info Row */}
            <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-3 opacity-10">
                        <OrderIcon className="w-16 h-16 text-blue-600" />
                    </div>
                    <p className="text-sm text-gray-500 font-medium mb-1">Panier Moyen</p>
                    <p className="text-2xl font-black text-gray-900">{stats.averageBasket} <span className="text-sm font-normal text-gray-400">DA</span></p>
                    <p className="text-xs text-gray-400 mt-1">Sur {stats.totalOrders} commandes</p>
                </div>
                <div onClick={() => setView({ screen: 'adminAccount' })} className="bg-gradient-to-br from-red-600 to-rose-700 p-5 rounded-2xl shadow-lg relative overflow-hidden cursor-pointer active:scale-95 transition-all">
                    <p className="text-[10px] text-white/60 font-black uppercase tracking-widest mb-1">Mon Compte</p>
                    <p className="text-lg font-black text-white leading-tight">Gérer mes <br />paramètres</p>
                    <div className="absolute bottom-2 right-2 p-1 bg-white/20 rounded-lg">
                        <AccountIcon className="w-5 h-5 text-white" />
                    </div>
                </div>
            </div>

            {/* Top Products */}
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 mb-6 mt-8">
                <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-6">
                    Top 3 Plats
                </h3>
                <div className="space-y-4">
                    {stats.topDishes.length > 0 ? (
                        stats.topDishes.map((dish, index) => (
                            <div key={dish.name} className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3 ${index === 0 ? 'bg-yellow-100 text-yellow-700' :
                                        index === 1 ? 'bg-gray-100 text-gray-700' : 'bg-orange-50 text-orange-700'
                                        }`}>
                                        {index + 1}
                                    </span>
                                    <span className="font-medium text-gray-800 text-sm truncate max-w-[150px]">{dish.name}</span>
                                </div>
                                <span className="font-bold text-gray-900 text-sm">{dish.count} ventes</span>
                            </div>
                        ))
                    ) : (
                        <p className="text-gray-400 text-sm italic text-center py-2">Pas encore de ventes</p>
                    )}
                </div>
            </div>

            {/* Peak Hours Chart */}
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="font-bold text-lg flex items-center">
                        <span className="bg-blue-100 p-2 rounded-xl mr-3 text-xl">📊</span>
                        Heures d'affluence
                    </h3>
                    <div className="px-3 py-1 bg-blue-50 rounded-full">
                        <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Temps Réel</p>
                    </div>
                </div>

                <div className="flex items-end justify-between h-40 gap-1.5 px-2">
                    {stats.hoursDistribution.slice(10, 23).map((count, index) => { // Showing 10h to 22h
                        const hour = index + 10;
                        const heightPercentage = Math.max((count / stats.maxOrdersInHour) * 100, 4); // Minimum height for visibility
                        const isPeak = count === stats.maxOrdersInHour && count > 0;

                        return (
                            <div key={hour} className="flex flex-col items-center flex-1 group relative">
                                {/* Bar Container */}
                                <div className="relative w-full flex justify-center h-full items-end">
                                    {/* Tooltip on hover */}
                                    <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] font-bold py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20 shadow-xl border border-gray-700">
                                        {count} commande{count > 1 ? 's' : ''}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                    </div>

                                    {/* The Bar */}
                                    <div
                                        style={{ height: `${heightPercentage}%` }}
                                        className={`w-full max-w-[14px] rounded-t-full transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${isPeak
                                            ? 'bg-gradient-to-t from-blue-600 to-blue-400 shadow-[0_0_15px_rgba(37,99,235,0.4)]'
                                            : count > 0 ? 'bg-gradient-to-t from-blue-200 to-blue-100 group-hover:from-blue-300 group-hover:to-blue-200' : 'bg-gray-50'}`}
                                    >
                                        {isPeak && <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-blue-600 rounded-full animate-ping"></div>}
                                    </div>
                                </div>

                                {/* Label */}
                                <div className={`mt-3 flex flex-col items-center`}>
                                    <span className={`text-[9px] uppercase tracking-tighter font-black ${isPeak ? 'text-blue-700' : 'text-gray-400'}`}>
                                        {hour}H
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-8 flex items-center justify-center gap-6">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Pic d'activité</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-100"></div>
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Activité normale</span>
                    </div>
                </div>
            </div>



        </div>
    );
};

export default AdminDashboardScreen;
